import axios from 'axios'
import URLSearchParams from '@ungap/url-search-params'
import { format, parse } from 'date-fns'

import {
  get as _get,
  find as _find,
  some as _some,
  snakeCase as _snakeCase
} from 'lodash'
import { fr } from 'date-fns/locale'

import careerPaths from '@/assets/data/careerPaths'
import contractTypes from '@/assets/data/contractTypes'
import { getLocale } from '@/i18n'
import {
  CRITERIA_COUNTRY,
  CRITERIA_CAREER_PATH,
  CRITERIA_CONTRACT_TYPE
} from '@/constants'

const dateFnsLocales = { fr }

/**
 * Format the date string
 * @param {String} date string
 */
function formatDate (date, inputFormat = 'yyyy-MM-dd', outFormat = 'PP') {
  const parsedDate = parse(date, inputFormat, new Date(), {
    locale: dateFnsLocales[getLocale()]
  })
  const formattedDate = format(parsedDate, outFormat, {
    locale: dateFnsLocales[getLocale()]
  })
  return formattedDate
}

/**
 * Format a job for frontend use
 * @param {Object} job an object from backend
 * @returns a formatted job object for frontend use
 */
function formatJob (job) {
  if (!job) return null
  const customFields = (job.customFields || []).filter(item => !!item.value)
  const customLovs = (job.customLovs || []).filter(item => !!item.value)
  const standardLovs = (job.standardLovs || []).filter(item => !!item.value)
  const country = _get(_find(customLovs, ({ criteria }) => {
    return _some(criteria, { type: CRITERIA_COUNTRY })
  }), 'criteria[0].label')
  const careerPath = _get(_find(customLovs, ({ criteria }) => {
    return _some(criteria, { type: CRITERIA_CAREER_PATH })
  }), 'criteria[0].label')
  const offerType = _get(_find(standardLovs, ({ criteria }) => {
    return _some(criteria, { type: CRITERIA_CONTRACT_TYPE })
  }), 'criteria[0].label')
  const postingStartDate = formatDate(job.postingStartDate)
  return Object.freeze({
    id: job.id,
    jobTitle: job.jobTitle,
    jobNumber: job.jobNumber,
    applicationUrl: job.applicationUrl,
    customFields,
    country,
    careerPath,
    offerType,
    postingStartDate
  })
}

/**
 * Get the filters from a querystring
 * @returns Array of {key, value}
 */
async function getFiltersFromQuery ({
  reference = '',
  searchpositiontype = 'all',
  searchjobgenerallist = 'all',
  country = '',
  search = ''
} = {}) {
  return [
    { key: 'global_reference_label', value: reference },
    { key: 'global_jobfamily_label', value: _get(_find(careerPaths, { value: searchpositiontype }), 'key') },
    { key: 'global_positiontype_label', value: _get(_find(contractTypes, { value: searchjobgenerallist }), 'key') },
    { key: 'global_country_label', value: _get(_find((await getCountryList()), { value: _snakeCase(country) }), 'key') || 'global_showall_label' },
    { key: 'global_search_label', value: search }
  ].filter(item => !!item.value)
}

/**
 * Get the country list based on results
 * @returns Array of {key, value}
 */
async function getCountryList () {
  const jobList = await getJobList()
  const countries = jobList
    .map(job => job.country)
  return [
    { key: 'global_showall_label', value: 'all' },
    ...countries
      .filter((v, i) => countries.indexOf(v) === i) // remove duplicates
      .map(country => { // convert to { key, value format }
        const key = country
        const value = _snakeCase(country)
        return { key, value }
      })
      .sort((a, b) => a.key.localeCompare(b.key))
  ]
}

/**
 * Get data from server
 * @param {Object} query a query object to pass to the endpoint
 */
async function getData (query = {}) {
  const form = new URLSearchParams({
    action: 'list',
    lang: getLocale().toUpperCase(),
    ...query
  })
  const response = await axios({
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded'
    },
    url: process.env.VUE_APP_API_ENDPOINT,
    data: form
  })
  return response.data
}

/**
 * Get the "fake" job that handles general application submission
 */
async function getGeneralApplication () {
  const data = await getData()
  return formatJob(data.generalapplication)
}

/**
 * Get the joblist formatted for FE
 */
async function getJobList () {
  const data = await getData()
  return (data.advertisements || []).map(job => formatJob(job)).filter(Boolean)
}

/**
 * Get the joblist formatted for FE, from a search query
 * @param {Object} query
 */
async function getJobListFromSearch (query) {
  const data = await getData(query)
  return (data.advertisements || [])
    .map(job => formatJob(job))
    .filter(job => {
      return !!job && // remove null results
        ((query.country === 'all') || _snakeCase(job.country) === _snakeCase(query.country)) // filter country because there is no filter BE side
    })
}

/**
 * Get a specific job formatted for FE from its ID
 * @param {String} id
 */
async function getJobDetail (id) {
  const data = await getData()
  const job = data.advertisements.find(item => {
    const itemIdString = item.id.toString()
    const idString = id.toString()
    return itemIdString === idString
  }) || false
  if (!job) {
    return false
  } else {
    const formattedJob = formatJob(job)
    const relatedJobs = data.advertisements
      .map(formatJob)
      .filter(relatedJob => ((relatedJob.careerPath === formattedJob.careerPath) && (relatedJob.id !== formattedJob.id)))
      .filter(Boolean)
    return {
      ...formattedJob,
      relatedJobs
    }
  }
}

/**
 * Post a subscribe alert to BE endpoint
 * @param {Object} query
 */
async function subscribeAlert (query = {}) {
  const form = new URLSearchParams({
    action: 'alert',
    lang: getLocale().toUpperCase(),
    ...query
  })

  const response = await axios({
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded'
    },
    url: process.env.VUE_APP_API_ENDPOINT,
    data: form
  })
  return response.data
}

/**
 * Post a unsubscribe alert to BE endpoint
 * @param {Object} query
 */
async function unsubscribe (query = {}) {
  const form = new URLSearchParams({
    action: 'unsubscribe',
    lang: getLocale().toUpperCase(),
    ...query
  })
  const response = await axios({
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded'
    },
    url: process.env.VUE_APP_API_ENDPOINT,
    data: form
  })
  return response.data
}

export {
  getData,
  getGeneralApplication,
  getJobList,
  getJobDetail,
  getJobListFromSearch,
  getFiltersFromQuery,
  subscribeAlert,
  unsubscribe,
  getCountryList
}
